import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { B2Button, B2Tabs } from "@hdb/biome-2";
import { FC, useEffect, useState } from "react";
import TableComponent from "../../../../../../core/components/table/table.component";
import SearchBarComponent from "../../../search-bar/search-bar.component";
import AssetUploadModal from "../asset-upload-modal.component";
import { RetentionPolicyEnum } from "src/core/enums/asset-enums";
import "./upload-progress-toaster.component.scss"
import ModalComponent from "src/core/components/modal/modal.component";

interface AssetErrorModalProps {
  isAssetModalOpen: boolean;
  handleCloseModal: () => void;
  uploadJobId: string | number | null;
  onDownloadValidationReport: (jobId: string | number) => void;
  selectedItems: any[];
  onConfirmAssetsFromErrorResults: () => void;
  fileUploadResponse: any[];
  modalNumberOfRecords: number;
  editableRows: any[];
  infoRows?: any[];
  retentionPolicy: RetentionPolicyEnum;
  setRetentionPolicy: (policy: RetentionPolicyEnum) => void;
  showConfirmationModal: boolean
  handleCloseConfirmationModal: () => void;
  modalActionConfig: any
  toBeDeletedCount: number
  phase: "idle" | "uploading" | "processing" | "complete" | "error" | "failed";
}

const AssetErrorModal: FC<AssetErrorModalProps> = ({
  isAssetModalOpen,
  handleCloseModal,
  uploadJobId,
  onDownloadValidationReport,
  selectedItems,
  onConfirmAssetsFromErrorResults,
  fileUploadResponse,
  modalNumberOfRecords,
  editableRows,
  infoRows = [],
  retentionPolicy,
  setRetentionPolicy,
  showConfirmationModal,
  handleCloseConfirmationModal,
  modalActionConfig,
  toBeDeletedCount,
  phase
}) => {
  // Internal state management
  const [activeAssetErrorTab, setActiveAssetErrorTab] =
    useState("Errors");
  const [modalPage, setModalPage] = useState(1);
  const [modalPageForEditable, setModalPageForEditable] = useState(1);
  const [tempEditableRows, setTempEditableRows] = useState<any[]>([]);
  const [tempFileUploadResponse, setTempFileUploadResponse] = useState<any[]>(
    [],
  );
  const [infoPage, setInfoPage] = useState(1);
  const [didUserConfirm, setDidUserConfirm] = useState(false);

  useEffect(() => {
    if (phase == "uploading") {
      setDidUserConfirm(false);
    }
  }, [phase])

  // const { uploadMode } = useSelector((state: any) => state.uploadProgress);

  // Static configurations
  const modalPageSize = 5;
  const modalColumns = [
    {
      title: "Asset",
      label: "assetCode",
      isSorted: true,
    },
    {
      title: "Message",
      label: "message",
    },
  ];
  const emptyConfig = {
    title: "No Data Found",
    description: "",
  };

  // Responsive position for SearchBar
  const [positionStyle, setPositionStyle] = useState<"absolute" | "relative">(
    typeof window !== "undefined" && window.innerWidth < 992
      ? "relative"
      : "absolute",
  );

  // Handle window resize for responsive positioning
  useEffect(() => {
    function handleResize() {
      setPositionStyle(window.innerWidth < 992 ? "relative" : "absolute");
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Initialize tempEditableRows when editableRows changes
  useEffect(() => {
    setTempEditableRows(editableRows);
  }, [editableRows]);

  // Pagination for fileUploadResponse
  const paginate = (array: any[], page_size: number, page_number: number) => {
    const data = array.slice(
      (page_number - 1) * page_size,
      page_number * page_size,
    );
    return data;
  };

  useEffect(() => {
    const data = paginate(fileUploadResponse, modalPageSize, modalPage);
    setTempFileUploadResponse(data);
  }, [modalPage, fileUploadResponse]);

  // Reset states when modal closes
  useEffect(() => {
    if (!isAssetModalOpen) {
      setActiveAssetErrorTab("Errors");
      setModalPage(1);
      setModalPageForEditable(1);
      setInfoPage(1);
      setTempEditableRows([]);
    }
  }, [isAssetModalOpen]);

  // Pagination handlers
  const onModalPageChange = (page: number) => {
    setModalPage(page);
  };

  const onModalPageChangeForEditable = (page: number) => {
    setModalPageForEditable(page);
  };

  const onModalSortChange = (sortBy: string, sortDirection: any) => {
    console.log("sortBy", sortBy, "sortDirection", sortDirection);
  };

  // Search/filter functions for tempEditableRows
  const filterEditableRows = (searchText: string) => {
    if (!searchText || searchText.trim() === "") {
      setTempEditableRows(editableRows);
    } else {
      const searchLower = searchText.toLowerCase();
      const filtered = editableRows.filter(
        (row: any) =>
          row.assetCode?.toLowerCase().includes(searchLower) ||
          row.message?.toLowerCase().includes(searchLower),
      );
      setTempEditableRows(filtered);
    }
  };

  const searchTempEditableRows = ({ searchText }: { searchText: string }) => {
    filterEditableRows(searchText);
    return { type: "success" };
  };

  const onSearchEditableRows = (searchValue: string) => {
    filterEditableRows(searchValue);
  };

  if (!isAssetModalOpen) {
    return null;
  }

  return (
    <>
      {isAssetModalOpen && (
        <AssetUploadModal
          showModal={isAssetModalOpen}
          handleClose={() => {
            handleCloseModal();
          }}
          subHeader={"Site wise select the assets to keep and save to continue uploading"}
        >
          <div style={{ padding: 16 }}>
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: "block", fontWeight: 600, marginBottom: 8 }}>
                How should this upload be applied?
              </div>

              <div className=" retention-options ">
                <label className={`card p-3 retention-card ${retentionPolicy === RetentionPolicyEnum.RETAIN_ASSETS_NOT_IN_FILE ? "border-primary" : ""}`} htmlFor="retain-assets-not-in-file" aria-label="Retain assets not in this file">
                  <div className="d-flex">
                    <div>
                      <input
                        id="retain-assets-not-in-file"
                        type="radio"
                        name="retentionPolicy"
                        checked={retentionPolicy === RetentionPolicyEnum.RETAIN_ASSETS_NOT_IN_FILE}
                        onChange={() => setRetentionPolicy(RetentionPolicyEnum.RETAIN_ASSETS_NOT_IN_FILE)}
                        className="me-2"
                      />
                    </div>
                    <div>
                      <div>Retain assets not in this file</div>
                      <div className="text-muted small">Add and update assets from this file. No existing assets will be deleted.This option also supports site/block-wise partial updates</div>
                    </div>
                  </div>
                </label>

                <label className={`card p-3 retention-card ${retentionPolicy === RetentionPolicyEnum.ONLY_RETAIN_ASSETS_IN_FILE ? "border-danger bg-danger-subtle" : ""}`} htmlFor="only-retain-assets-in-file" aria-label="Only retain assets in this file">
                  <div className="d-flex">
                    <div>
                      <input
                        id="only-retain-assets-in-file"
                        type="radio"
                        name="retentionPolicy"
                        checked={retentionPolicy === RetentionPolicyEnum.ONLY_RETAIN_ASSETS_IN_FILE}
                        onChange={() => setRetentionPolicy(RetentionPolicyEnum.ONLY_RETAIN_ASSETS_IN_FILE)}
                        className="me-2"
                      />
                    </div>
                    <div>
                      <div>Only retain the assets of this file</div>
                      <div className="text-muted small">Permanently delete every asset across the site that isn't in this file</div>
                    </div>
                  </div>
                </label>
              </div>
              {retentionPolicy === RetentionPolicyEnum.ONLY_RETAIN_ASSETS_IN_FILE && (
                <div
                  className="mt-3 p-3 border border-danger rounded"
                  style={{
                    backgroundColor: "#fff5f5",
                  }}
                >
                  <div className="d-flex align-items-center gap-3">

                    <div
                      style={{
                        fontSize: "32px",
                        fontWeight: 700,
                        color: "#dc3545",
                      }}
                    >
                      {toBeDeletedCount.toLocaleString()}
                    </div>

                    <div>
                      <span className="text-danger fw-bold">
                        assets will be permanently deleted.
                      </span>

                      <div className="mt-2">
                        <input
                          type="checkbox"
                          checked={didUserConfirm}
                          onChange={(e) => setDidUserConfirm(e.target.checked)}
                          className="me-2"
                        />
                        {" "}I understand{" "}
                        <strong> {toBeDeletedCount?.toLocaleString()} assets</strong> will be permanently deleted.
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="d-flex flex-row justify-content-between ms-3 align-items-center">
            <div>
              <div style={{ fontSize: "18px", fontWeight: "700" }}>
                <div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
                  Asset error details
                </div>
                {activeAssetErrorTab === "Warning" && (
                  <div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
                    Select Assets and click save to continue uploading
                  </div>
                )}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                height: "40px",
                gap: "10px",
              }}
              className="my-3"
            >
              {uploadJobId && (
                <B2Button
                  variant="primary"
                  onClick={() => {
                    onDownloadValidationReport(uploadJobId);
                  }}
                >
                  <FontAwesomeIcon icon={faDownload} className="me-2" />
                  Export Asset Error
                </B2Button>
              )}
              {selectedItems?.length > 1 && (
                <B2Button
                  variant="primary"
                  onClick={() => {
                    onConfirmAssetsFromErrorResults();
                  }}
                  disabled={retentionPolicy === RetentionPolicyEnum.ONLY_RETAIN_ASSETS_IN_FILE && !didUserConfirm}
                >
                  Save
                </B2Button>
              )}
            </div>
          </div>

          <div className="custom_tab_start" style={{ position: "relative" }}>
            <B2Tabs
              activeKey={activeAssetErrorTab}
              pills
              tabClassName={""}
              onSelect={(selectedId: any) => {
                setActiveAssetErrorTab(selectedId);
              }}
              tab={[
                {
                  content: (
                    <div>
                      {
                        <TableComponent
                          tableHeaders={modalColumns}
                          emptyConfig={emptyConfig}
                          data={tempFileUploadResponse}
                          totalNumberOfRecords={modalNumberOfRecords}
                          pageSize={modalPageSize}
                          current={modalPage}
                          onChangePage={onModalPageChange}
                          onChangeSort={onModalSortChange}
                        />
                      }
                    </div>
                  ),
                  id: "Errors",
                  title: `Errors (${fileUploadResponse?.length})`,
                },
                ...(editableRows?.length > 0
                  ? [
                    {
                      content: (
                        <div>
                          <div
                            className="d-flex justify-content-end"
                            style={{
                              position: positionStyle as "absolute" | "relative",
                              zIndex: 999,
                              top: 0,
                              right: 0,
                            }}
                          >
                            <SearchBarComponent
                              placeholder="Search Error Messages"
                              methodToDispatch={searchTempEditableRows}
                              getSearch={onSearchEditableRows}
                              isDynamicSearch={true}
                            />
                          </div>
                          <div style={{ height: "300px", overflowY: "auto" }}>
                            <TableComponent
                              tableHeaders={modalColumns}
                              emptyConfig={emptyConfig}
                              // allData={editableRows}
                              data={tempEditableRows}
                              totalNumberOfRecords={modalNumberOfRecords}
                              pageSize={editableRows?.length}
                              current={modalPageForEditable}
                              onChangePage={onModalPageChangeForEditable}
                              onChangeSort={onModalSortChange}
                              enableSelection={true}
                              autoSelectAll={true}
                            />
                          </div>
                        </div>
                      ),
                      id: "Warning",
                      title: `Warning (${tempEditableRows?.length})`,
                    },
                  ]
                  : []),
                ...(infoRows?.length > 0
                  ? [
                    {
                      content: (
                        <div>
                          <TableComponent
                            tableHeaders={modalColumns}
                            emptyConfig={emptyConfig}
                            data={paginate(infoRows, modalPageSize, infoPage)}
                            totalNumberOfRecords={infoRows.length}
                            pageSize={modalPageSize}
                            current={infoPage}
                            onChangePage={setInfoPage}
                            onChangeSort={onModalSortChange}
                          />
                        </div>
                      ),
                      id: "Info",
                      title: `Info (${infoRows.length})`,
                    },
                  ]
                  : []),
              ]}
            />
          </div>
        </AssetUploadModal>
      )}

      <ModalComponent
        isConformationModal={true}
        showModal={showConfirmationModal}
        handleClose={handleCloseConfirmationModal}
        headerTitle={"Abort this upload?"}
        actionConfig={modalActionConfig}
        isClosable={false}
        backdrop={"static"}
        negativeButtonText="Keep Reviewing"
      >
        <h5 className="text-center">
          Closing now will <b className="text-danger">Permanently Abort</b> the operation. The uploaded rows will be discarded and nothing will be saved
        </h5>
      </ModalComponent>
    </>

  );
};

export default AssetErrorModal;
